// Human-readable labels for audit-log action codes.

export type AuditCodeTone = 'normal' | 'quiet' | 'warning';

const LABELS: Record<string, string> = {
  LOGIN_SUCCEEDED: 'Logged in',
  LOGIN_FAILED: 'Sign-in failed',
  LOGOUT: 'Signed out',
  MFA_ENROLLED: 'Enrolled TOTP',
  MFA_RESET: 'Reset TOTP',
  PASSKEY_REGISTERED: 'Added passkey',
  PASSKEY_REMOVED: 'Removed passkey',
  PASSWORD_RESET_REQUESTED: 'Requested password reset',
  PASSWORD_RESET_COMPLETED: 'Reset password',
  MASTER_SCOPE_USED: 'Used tenant context',
  MASTER_SCOPE_ENTERED: 'Opened legacy tenant view',
  TENANT_CREATED: 'Added tenant',
  TENANT_UPDATED: 'Edited tenant',
  TENANT_SUSPENDED: 'Suspended tenant',
  TENANT_RESTORED: 'Restored tenant',
  TENANT_ARCHIVED: 'Archived tenant',
  EMPLOYEE_CREATED: 'Added employee',
  EMPLOYEE_UPDATED: 'Edited employee',
  EMPLOYEE_INVITED: 'Invited employee',
  EMPLOYEE_SUSPENDED: 'Suspended employee',
  EMPLOYEE_REACTIVATED: 'Reactivated employee',
  EMPLOYEE_OFFBOARDED: 'Deleted employee',
  EMPLOYEE_BULK_IMPORTED: 'Imported employees',
  RESIDENT_CREATED: 'Added resident',
  RESIDENT_INVITED: 'Invited resident',
  RESIDENT_UPDATED: 'Edited resident',
  RESIDENT_MOVED_IN: 'Moved resident in',
  RESIDENT_MOVED_OUT: 'Moved resident out',
  SCHEDULE_CREATED: 'Added schedule',
  SCHEDULE_UPDATED: 'Edited schedule',
  SCHEDULE_DELETED: 'Deleted schedule',
  ACCESS_GROUP_CREATED: 'Added access group',
  ACCESS_GROUP_UPDATED: 'Edited access group',
  ACCESS_GROUP_DELETED: 'Deleted access group',
  ACCESS_GROUP_MEMBER_ADDED: 'Granted access',
  ACCESS_GROUP_MEMBER_REMOVED: 'Revoked access',
  ACCESS_GROUP_DRIFT_DETECTED: 'Access zone needs review',
  ACCESS_GROUP_RESYNC_REQUESTED: 'Requested access zone update',
  ACCESS_GROUP_MARK_RECONCILED: 'Marked access zone resolved',
  HARDWARE_SCOPE_ASSIGNED: 'Assigned hardware',
  HARDWARE_SCOPE_RELEASED: 'Released hardware',
  BUILDING_RESOURCES_REFRESHED: 'Refreshed hardware inventory',
  TENANT_RESOURCE_GRANTED: 'Granted hardware access',
  TENANT_RESOURCE_REVOKED: 'Revoked hardware access',
  ACCESS_ZONE_CREATED: 'Added access zone',
  ACCESS_ZONE_RESOURCES_REPLACED: 'Updated access zone doors',
  ACCESS_ZONE_ASSIGNED: 'Assigned access zone',
  ACCESS_ZONE_UNASSIGNED: 'Removed access zone',
  DOOR_UNLOCKED: 'Unlocked door',
  LOCKDOWN_ENGAGED: 'Engaged lockdown',
  LOCKDOWN_CLEARED: 'Cleared lockdown',
  WEBHOOK_REGISTERED: 'Registered webhook',
  WEBHOOK_DELETED: 'Removed webhook',
  OUTBOX_RETRIED: 'Retried update',
  OUTBOX_DEAD_LETTERED: 'Update needs attention',
  CALLBOX_ENTRY_CREATED: 'Added callbox entry',
  CALLBOX_ENTRY_UPDATED: 'Edited callbox entry',
  CALLBOX_ENTRY_DELETED: 'Removed callbox entry',
  CALLBOX_GROUP_CREATED: 'Created callbox directory',
  CALLBOX_GROUP_UPDATED: 'Updated callbox directory',
  CALLBOX_GROUP_DELETED: 'Removed callbox directory',
  CALLBOX_TENANT_ENTRY_UPSERTED: 'Callbox tenant listing saved',
  CALLBOX_DEVICE_CREATED: 'Registered callbox',
  CALLBOX_DEVICE_UPDATED: 'Updated callbox',
  CALLBOX_DEVICE_RING_TARGET_UPDATED: 'Updated callbox button routing',
  CALLBOX_DEVICE_DELETED: 'Removed callbox',
  CALLBOX_DEVICE_ASSIGNED: 'Assigned callbox',
  CALLBOX_DEVICE_UNASSIGNED: 'Unassigned callbox',
  CALLBOX_DEVICE_REFRESHED: 'Refreshed callboxes',
  CALLBOX_DEVICE_SYNCED: 'Callbox updated',
  CALLBOX_DEVICES_SYNCED: 'Updated callboxes',
  CALLBOX_DIRECTORY_MEMBER_CREATED: 'Added callbox listing',
  CALLBOX_DIRECTORY_MEMBER_UPDATED: 'Updated callbox listing',
  CALLBOX_DIRECTORY_MEMBER_DELETED: 'Removed callbox listing',
  CREDENTIAL_ISSUED: 'Credential added',
  CREDENTIAL_REVOKED: 'Credential removed',
  CREDENTIAL_POLICY_UPDATED: 'Credential policy updated',
  LOCKDOWN_ACTIVATED: 'Emergency lockdown activated',
  OUTBOX_JOB_RETRIED: 'Update retried',
  OUTBOX_JOB_DELETED: 'Update removed',
  USER_INVITED: 'Invited manager',
  USER_ROLE_CHANGED: 'Manager role changed',
};

const QUIET_CODES = new Set<string>(['MASTER_SCOPE_USED', 'MASTER_SCOPE_ENTERED']);

const WARNING_CODES = new Set<string>([
  'LOGIN_FAILED',
  'TENANT_SUSPENDED',
  'EMPLOYEE_SUSPENDED',
  'EMPLOYEE_OFFBOARDED',
  'ACCESS_GROUP_DRIFT_DETECTED',
  'LOCKDOWN_ENGAGED',
  'OUTBOX_DEAD_LETTERED',
]);

const NOISE_CODES = new Set<string>([
  'MASTER_SCOPE_USED',
  'BUILDING_RESOURCES_REFRESHED',
]);

export function humanizeAuditCode(action: string): string {
  // eslint-disable-next-line security/detect-object-injection
  const known = LABELS[action];
  if (known) return known;
  const lc = action.toLowerCase().replace(/_/g, ' ');
  return lc.charAt(0).toUpperCase() + lc.slice(1);
}

export function auditCodeTone(action: string): AuditCodeTone {
  if (QUIET_CODES.has(action)) return 'quiet';
  if (WARNING_CODES.has(action)) return 'warning';
  return 'normal';
}

export function isDashboardNoise(action: string): boolean {
  return NOISE_CODES.has(action);
}

export interface AuditDisplayRow {
  action: string;
  actor_name?: string | null;
  actor_user_id?: string | null;
  tenant_name?: string | null;
  resource_type?: string | null;
}

function compactPersonLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return trimmed;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? trimmed;
  const first = parts[0] ?? trimmed;
  const last = parts[parts.length - 1] ?? '';
  const lastInitial = last.charAt(0).toUpperCase();
  return lastInitial ? `${first} ${lastInitial}.` : first;
}

export function auditActorLabel(row: AuditDisplayRow): string {
  if (row.actor_name?.trim()) return compactPersonLabel(row.actor_name);
  if (row.actor_user_id) return row.actor_user_id.slice(0, 8);
  return 'System';
}

export function compactAuditActorContext(row: AuditDisplayRow): string {
  const actor = auditActorLabel(row);
  const tenant = row.tenant_name?.trim();
  if (tenant && actor !== 'System') return `${tenant} - ${actor}`;
  return actor;
}

export function formatAuditSummary(row: AuditDisplayRow): string {
  const who = auditActorLabel(row);
  const action = humanizeAuditCode(row.action);
  const tenant = row.tenant_name?.trim();
  return tenant ? `${who} · ${tenant} · ${action}` : `${who} · ${action}`;
}

export function formatCompactAuditSummary(row: AuditDisplayRow): string {
  const who = compactAuditActorContext(row);
  const action = humanizeAuditCode(row.action);
  return `${who} · ${action}`;
}

/** Raw API audit row shape used by activity log and detail fetchers. */
export interface ApiAuditRow {
  id: string;
  action: string;
  created_at: string;
  actor_user_id: string | null;
  actor_name?: string | null;
  tenant_name?: string | null;
  resource_type: string;
  resource_id: string | null;
}

export interface MappedAuditEntry {
  id: string;
  action: string;
  created_at: string;
  action_label: string;
  actor_label: string;
  tenant_label: string | null;
  summary: string;
}

export function mapAuditRowToEntry(row: ApiAuditRow): MappedAuditEntry {
  return {
    id: row.id,
    action: row.action,
    created_at: row.created_at,
    action_label: humanizeAuditCode(row.action),
    actor_label: auditActorLabel(row),
    tenant_label: row.tenant_name?.trim() || null,
    summary: formatAuditSummary(row),
  };
}

/** Shared fixture for tests and Playwright mocks — single source of truth. */
export const AUDIT_FIXTURE_JOHN_SMITH_EMPLOYEE_UPDATED: ApiAuditRow = {
  id: 'a1',
  action: 'EMPLOYEE_UPDATED',
  resource_type: 'employee',
  resource_id: 'e1',
  actor_user_id: 'u1',
  actor_name: 'John Smith',
  tenant_name: 'marathon',
  created_at: '2026-06-23T12:00:00.000Z',
};

export const AUDIT_FIXTURE_SUMMARY_JOHN_SMITH = formatAuditSummary(
  AUDIT_FIXTURE_JOHN_SMITH_EMPLOYEE_UPDATED,
);
